import * as React from 'react'
import { CloseIcon } from '@/src/icons'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'
import { Button } from '@/registry/reapit/ui/button'

// ---------------------------------------------------------------------------
// Dialog (Root)
// ---------------------------------------------------------------------------

function Dialog({ onOpenChange, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" onOpenChange={onOpenChange} {...props} />
}

// ---------------------------------------------------------------------------
// DialogTrigger
// ---------------------------------------------------------------------------

function DialogTrigger({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof DialogPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
}) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...asChildProps(asChild, children)} {...props} />
}

// ---------------------------------------------------------------------------
// DialogPortal
// ---------------------------------------------------------------------------

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

// ---------------------------------------------------------------------------
// DialogClose
// ---------------------------------------------------------------------------

function DialogClose({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof DialogPrimitive.Close>, 'render'> & {
  asChild?: boolean
}) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...asChildProps(asChild, children)} {...props} />
}

// ---------------------------------------------------------------------------
// DialogOverlay (maps to Base UI Backdrop)
// ---------------------------------------------------------------------------

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 transition-opacity duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DialogContent (maps to Base UI Portal > Backdrop + Popup)
// ---------------------------------------------------------------------------

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'medium',
  ...props
}: Omit<React.ComponentProps<typeof DialogPrimitive.Popup>, 'render'> & {
  showCloseButton?: boolean
  size?: 'small' | 'medium' | 'large' | 'full-screen-xs'
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-size={size}
        className={cn(
          // DS: centred overlay, bg-background surface, 8 px radius, border, shadow.
          // Max-height 85dvh with internal scroll to match DS resizing rules.
          // Transition-based animation: opacity + scale, driven by data-[starting-style] / data-[ending-style].
          'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg outline-none max-h-[85dvh] overflow-y-auto transition-[opacity,scale] duration-200 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
          size === 'small' && 'sm:max-w-xs',
          size === 'medium' && 'sm:max-w-lg',
          size === 'large' && 'sm:max-w-2xl',
          // DS full-screen XS: fills the viewport on mobile, fixed 375 px panel on SM+.
          size === 'full-screen-xs' &&
            'h-dvh max-h-dvh w-screen max-w-none rounded-none sm:h-auto sm:max-h-[calc(100dvh-4rem)] sm:w-[375px] sm:rounded-lg',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          // DS: close icon is icon-md (size-5 = 20 px). Only shown when no footer.
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5"
          >
            <CloseIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

// ---------------------------------------------------------------------------
// DialogHeader / DialogFooter (plain HTML wrappers — unchanged)
// ---------------------------------------------------------------------------

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant="outline">Close</Button>} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DialogTitle / DialogDescription
// ---------------------------------------------------------------------------

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    // DS typography: heading level L = 20 px / semi-bold (text-lg = 20px = DS L, font-semibold).
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-7 font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
